import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';
import { Button, Drawer, ErrorNotice, Field, Pill, SelectInput, SkeletonTable, TextInput } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  RESOURCES_QUERY_KEY,
  RESOURCE_LIMITS,
  RESOURCE_TOPICS_QUERY_KEY,
  RESOURCE_TOPIC_ICONS,
  createResourceTopic,
  listResourceTopics,
  updateResourceTopic,
  type ResourceTopic,
  type ResourceTopicIcon,
} from '@/lib/resources';

/** The icon choices, in the words the select shows. */
const ICON_LABELS: Readonly<Record<ResourceTopicIcon, string>> = {
  sparkles: 'Sparkles',
  lightbulb: 'Light bulb',
  mic: 'Microphone',
  'circle-help': 'Question mark',
  messages: 'Speech bubbles',
  compass: 'Compass',
  megaphone: 'Megaphone',
  'heart-handshake': 'Handshake',
  'book-marked': 'Bookmarked book',
  wifi: 'Wi-Fi',
  'file-text': 'Document',
  'book-open': 'Open book',
};

const iconOf = (value: string): ResourceTopicIcon =>
  RESOURCE_TOPIC_ICONS.find((icon) => icon === value) ?? 'book-open';

function IconSelect({ id, value, onChange }: { readonly id: string; readonly value: ResourceTopicIcon; readonly onChange: (icon: ResourceTopicIcon) => void }): React.ReactElement {
  return (
    <SelectInput id={id} value={value} onChange={(event) => onChange(iconOf(event.target.value))}>
      {RESOURCE_TOPIC_ICONS.map((icon) => (
        <option key={icon} value={icon}>
          {ICON_LABELS[icon]}
        </option>
      ))}
    </SelectInput>
  );
}

/**
 * One topic: rename it, describe it, pick its tile icon, move it up or down,
 * archive it or bring it back. Topics are never deleted.
 */
function TopicRow({
  topic,
  above,
  below,
}: {
  readonly topic: ResourceTopic;
  readonly above: ResourceTopic | undefined;
  readonly below: ResourceTopic | undefined;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [name, setName] = useState(topic.name);
  const [description, setDescription] = useState(topic.description);
  const [icon, setIcon] = useState<ResourceTopicIcon>(topic.icon);
  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: RESOURCE_TOPICS_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: RESOURCES_QUERY_KEY });
  };
  const save = useMutation({
    mutationFn: (changes: Parameters<typeof updateResourceTopic>[1]) => updateResourceTopic(topic.id, changes),
    onSuccess: refresh,
  });
  // Swapping sort orders with the neighbor moves a topic one place.
  const move = useMutation({
    mutationFn: async (neighbor: ResourceTopic) => {
      await updateResourceTopic(topic.id, { sortOrder: neighbor.sortOrder });
      await updateResourceTopic(neighbor.id, { sortOrder: topic.sortOrder });
    },
    onSuccess: refresh,
  });
  const changed = name.trim() !== topic.name || description.trim() !== topic.description || icon !== topic.icon;
  const prefix = `topic-${topic.id}`;

  return (
    <li className="space-y-3 border-t border-portal-line py-4 first:border-t-0 first:pt-0">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <b className="text-portal-small">{topic.name}</b>
          <span className="text-portal-micro text-portal-muted">
            {topic.resourceCount} resource{topic.resourceCount === 1 ? '' : 's'}
          </span>
          {topic.archived ? <Pill tone="neutral">Archived</Pill> : null}
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="compact" aria-label={`Move ${topic.name} up`} disabled={!above || move.isPending} onClick={() => above && move.mutate(above)}>
            <ArrowUpIcon aria-hidden className="size-4" />
          </Button>
          <Button variant="ghost" size="compact" aria-label={`Move ${topic.name} down`} disabled={!below || move.isPending} onClick={() => below && move.mutate(below)}>
            <ArrowDownIcon aria-hidden className="size-4" />
          </Button>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" htmlFor={`${prefix}-name`} required>
          <TextInput id={`${prefix}-name`} maxLength={RESOURCE_LIMITS.topicName} value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="Tile icon" htmlFor={`${prefix}-icon`}>
          <IconSelect id={`${prefix}-icon`} value={icon} onChange={setIcon} />
        </Field>
      </div>
      <Field label="Description" htmlFor={`${prefix}-description`} hint="Shown under the topic’s title when a client opens it.">
        <TextInput
          id={`${prefix}-description`}
          maxLength={RESOURCE_LIMITS.topicDescription}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="secondary" size="compact" disabled={save.isPending} onClick={() => save.mutate({ archived: !topic.archived })}>
          {topic.archived ? 'Bring back' : 'Archive'}
        </Button>
        <Button size="compact" disabled={!changed || name.trim() === '' || save.isPending} onClick={() => save.mutate({ name: name.trim(), description: description.trim(), icon })}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
      {save.isError ? <ErrorNotice inline title="Topic not saved" message={errorMessage(save.error)} /> : null}
      {move.isError ? <ErrorNotice inline title="Topic not moved" message={errorMessage(move.error)} /> : null}
    </li>
  );
}

/**
 * The Topics drawer on the Resources list: rename, reorder, describe, archive
 * or add topics. A topic is archived, never deleted, so the resources under it
 * keep a name; an archived topic is no longer offered for new resources and
 * leaves the portal's chips.
 *
 * @param props - Whether it is open, and how to close it.
 * @returns The drawer.
 */
export function TopicsDrawer({ open, onClose }: { readonly open: boolean; readonly onClose: () => void }): React.ReactElement {
  const queryClient = useQueryClient();
  const topics = useQuery({ queryKey: RESOURCE_TOPICS_QUERY_KEY, queryFn: ({ signal }) => listResourceTopics(signal), enabled: open });
  const [name, setName] = useState('');
  const [icon, setIcon] = useState<ResourceTopicIcon>('book-open');
  const add = useMutation({
    mutationFn: () => createResourceTopic({ name: name.trim(), description: '', icon }),
    onSuccess: async () => {
      setName('');
      await queryClient.invalidateQueries({ queryKey: RESOURCE_TOPICS_QUERY_KEY });
    },
  });
  const list = topics.data ?? [];

  return (
    <Drawer
      open={open}
      size="wide"
      title="Topics"
      subtitle="Every resource is filed under one topic. Clients browse by topic in their Resource center."
      onClose={onClose}
    >
      <div className="space-y-6">
        {topics.isError ? (
          <ErrorNotice title="Couldn’t load the topics" message={errorMessage(topics.error)} />
        ) : topics.isPending ? (
          <SkeletonTable rows={5} columns={['w-[60%]', 'w-[30%]']} label="Loading topics…" />
        ) : (
          <ul data-guide="topics-list">
            {list.map((topic, index) => (
              <TopicRow key={`${topic.id}-${topic.sortOrder}`} topic={topic} above={list[index - 1]} below={list[index + 1]} />
            ))}
          </ul>
        )}

        <section data-guide="topics-add" aria-label="Add a topic" className="space-y-3 rounded-portal-card border border-portal-line bg-portal-bg p-4">
          <p className="text-portal-small font-bold">Add a topic</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field guide="topics-add-name" label="Name" htmlFor="new-topic-name" required>
              <TextInput id="new-topic-name" maxLength={RESOURCE_LIMITS.topicName} value={name} onChange={(event) => setName(event.target.value)} />
            </Field>
            <Field label="Tile icon" htmlFor="new-topic-icon">
              <IconSelect id="new-topic-icon" value={icon} onChange={setIcon} />
            </Field>
          </div>
          <div className="flex justify-end">
            <Button guide="topics-add-submit" disabled={name.trim() === '' || add.isPending} onClick={() => add.mutate()}>
              {add.isPending ? 'Adding…' : 'Add topic'}
            </Button>
          </div>
          {add.isError ? <ErrorNotice inline title="Topic not added" message={errorMessage(add.error)} /> : null}
        </section>
      </div>
    </Drawer>
  );
}
